/**
 * Dispatcher: maps a ConditionNode's type to the correct renderer component.
 * Matches ~/code/ui condition-editor-v2 render-node.gts
 *
 * ConditionGroup is co-located here because the two components are mutually
 * recursive — group renders nodes, nodes can be groups — and living in the
 * same module avoids a circular import.
 */

import { useState } from 'react';
import { Box, Flex, Text, Stack, Badge, Icon, PlainButton } from '@customerio/pluma-components/react';
import type { ConditionNode as ConditionNodeType, GroupNode } from '../types';
import { AttributeCondition } from './renderers/attribute';
import { EventCondition } from './renderers/event';
import { DeviceCondition } from './renderers/device';
import { PageviewCondition } from './renderers/pageview';
import { ScreenviewCondition } from './renderers/screenview';
import { FormCondition } from './renderers/form';
import { InboundCondition } from './renderers/inbound';
import { OptOutCondition } from './renderers/opt-out';
import { MessageCondition } from './renderers/message';
import { SegmentCondition } from './renderers/segment';
import { RelationshipCondition } from './renderers/relationship';
import { UnknownCondition } from './renderers/unknown';
import * as groupStyles from './condition-group.css';

// =============================================================================
// ConditionGroup — renders a group node (and/or) with recursive children
// =============================================================================

interface ConditionGroupProps {
  node: GroupNode;
  depth?: number;
  workspaceId: number;
}

export function ConditionGroup({ node, depth = 0, workspaceId }: ConditionGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { children, operator } = node;

  if (children.length === 0) {
    return (
      <Text size="s" color="subtle">
        No conditions
      </Text>
    );
  }

  // Single child — render directly without group wrapper
  if (children.length === 1) {
    return <ConditionNodeRenderer node={children[0]} depth={depth} workspaceId={workspaceId} />;
  }

  const groupLabel = operator === 'and' ? 'all' : 'any';

  return (
    <Box w="full" className={depth > 0 ? groupStyles.nestedGroup : undefined}>
      {/* Descriptor: "Matches all/any of N conditions" */}
      <Flex gap="100" alignItems="center" mb={isExpanded ? '150' : '0'} justifyContent="space-between" w="full">
        <Flex gap="050" alignItems="center">
          {depth > 0 ? (
            <PlainButton
              cursor="pointer"
              display="inline-flex"
              alignItems="center"
              aria-label={isExpanded ? 'Collapse conditions' : 'Expand conditions'}
              onClick={() => setIsExpanded((v) => !v)}
            >
              <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} size="sm" />
            </PlainButton>
          ) : null}
          <Text size="xs" color="placeholder" display="inline-flex" gap="050" alignItems="center">
            Matches <Badge>{groupLabel}</Badge> of {children.length} conditions
          </Text>
        </Flex>
      </Flex>

      {isExpanded ? (
        <Stack gap="150">
          {children.map((child, index) => (
            <ConditionNodeRenderer
              key={`${child.type}-${index}`}
              node={child}
              depth={depth + 1}
              workspaceId={workspaceId}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

// =============================================================================
// ConditionNodeRenderer — dispatches a node to the correct renderer
// =============================================================================

interface ConditionNodeProps {
  node: ConditionNodeType;
  depth?: number;
  workspaceId: number;
}

export function ConditionNodeRenderer({ node, depth = 0, workspaceId }: ConditionNodeProps) {
  switch (node.type) {
    case 'group':
      return <ConditionGroup node={node} depth={depth} workspaceId={workspaceId} />;
    case 'attribute':
      return <AttributeCondition node={node} />;
    case 'event':
      return <EventCondition node={node} />;
    case 'segment':
      return <SegmentCondition node={node} workspaceId={workspaceId} renderCondition={ConditionNodeRenderer} />;
    case 'device':
      return <DeviceCondition node={node} />;
    case 'pageview':
      return <PageviewCondition node={node} />;
    case 'screenview':
      return <ScreenviewCondition node={node} />;
    case 'form':
      return <FormCondition node={node} />;
    case 'message':
      return <MessageCondition node={node} workspaceId={workspaceId} />;
    case 'inbound':
      return <InboundCondition node={node} />;
    case 'opt_out':
      return <OptOutCondition node={node} />;
    case 'relationship':
      return <RelationshipCondition node={node} />;
    case 'unknown':
      return <UnknownCondition node={node} />;
    default: {
      const _exhaustive: never = node;
      return <UnknownCondition node={{ type: 'unknown', raw: _exhaustive as Record<string, unknown> }} />;
    }
  }
}
